/**
 * NearbyStoresRail — open-now nearby stores in a card list. Shows rating,
 * delivery ETA, free-delivery threshold, and a "Visit" CTA per store.
 *
 * Replaces the inline JSX previously in CustomerHome.tsx.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, ChevronRight, Zap } from 'lucide-react';
import { SectionHeader, Badge, RatingStars, Skeleton, css } from '../../design';
import { Store } from '../../api/stores';

export interface NearbyStoresRailProps {
  stores: Store[];
  loading?: boolean;
  /** Max stores rendered. Defaults to 5. */
  limit?: number;
}

const NearbyStoresRail: React.FC<NearbyStoresRailProps> = ({ stores, loading, limit = 5 }) => {
  const navigate = useNavigate();
  const visible = stores.slice(0, limit);

  if (loading) {
    return (
      <section style={{ marginBottom: 24 }}>
        <SectionHeader title="🏪 Nearby stores" subtitle="Hyperlocal delivery in under 15 min" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton.Card key={i} height={64} />
          ))}
        </div>
      </section>
    );
  }

  if (visible.length === 0) return null;

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeader
        title="🏪 Nearby stores"
        subtitle="Hyperlocal delivery in under 15 min"
        actionLabel="See all"
        onAction={() => navigate('/stores')}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visible.map((store) => (
          <button
            key={store.id}
            type="button"
            onClick={() => navigate(`/store/${store.id}`)}
            style={{
              background: css.color.surface,
              border: `1px solid ${css.color.border}`,
              borderRadius: 14,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease',
              boxShadow: css.shadow.sm,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = css.shadow.cardHover;
              e.currentTarget.style.borderColor = css.color.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = css.shadow.sm;
              e.currentTarget.style.borderColor = css.color.border;
            }}
          >
            {/* Avatar */}
            <div
              aria-hidden
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${css.color.primarySoft}, ${css.color.surface2})`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: css.color.primary,
                fontWeight: 800,
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              {store.business_name.slice(0, 2).toUpperCase()}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: css.color.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 220,
                  }}
                >
                  {store.business_name}
                </div>
                {store.is_open ? (
                  <Badge variant="success" size="sm">Open</Badge>
                ) : (
                  <Badge variant="danger" size="sm">Closed</Badge>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  fontSize: 12,
                  color: css.color.textMuted,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {(store as any).rating != null && (store as any).rating > 0 && (
                  <RatingStars rating={(store as any).rating} variant="chip" size={12} />
                )}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <MapPin size={12} /> {store.city || store.address?.slice(0, 30) || 'Local store'}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Clock size={12} /> {store.opening_hours || '8 AM'} – {store.closing_hours || '10 PM'}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: css.color.primary, fontWeight: 600 }}>
                  <Zap size={12} /> 12 min
                </span>
              </div>
            </div>

            <ChevronRight size={18} color={css.color.textMuted} />
          </button>
        ))}
      </div>
    </section>
  );
};

export default NearbyStoresRail;

